import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { getApiUrl, fetchWithAuth } from '../../../api'

interface PageListItem {
  id: number
  slug: string
  title: string
  is_visible: number
  sort_order: number
  created_at: string
}

export default function AdminPageList() {
  const navigate = useNavigate()
  const [pages, setPages] = useState<PageListItem[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchPages()
  }, [])

  const fetchPages = async () => {
    setLoading(true)
    try {
        const res = await fetchWithAuth(getApiUrl('/marubit/api/page'))
        if (!res.ok) throw new Error('조회 실패')
      setPages(await res.json())
    } catch {
      alert('목록을 불러오는 데 실패했습니다.')
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (id: number, title: string) => {
    if (!window.confirm(`"${title}" 페이지를 삭제하시겠습니까?`)) return
    try {
      const token = localStorage.getItem('access_token')
      const res = await fetch(getApiUrl(`/marubit/api/page/admin/${id}`), {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) {
        const err = await res.json().catch(() => null)
        throw new Error(err?.detail ?? '삭제 실패')
      }
      fetchPages()
    } catch (e: any) {
      alert(e.message ?? '삭제에 실패했습니다.')
    }
  }

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('ko-KR', {
      year: 'numeric', month: '2-digit', day: '2-digit',
    })

  if (loading) return (
    <div className="min-h-[60vh] flex items-center justify-center text-gray-300">
      불러오는 중...
    </div>
  )

  return (
    <div className="bg-[#f5f2f0] min-h-screen p-8">
      <div className="max-w-[1200px] mx-auto">

        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-[22px] font-extrabold text-[#2a1f1a] mb-1">페이지 관리</h1>
            <p className="text-[13px] text-gray-400">
              {new Date().toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' })}
            </p>
          </div>
          <button
            onClick={() => navigate('/admin/page/new')}
            className="bg-[#887267] text-white text-[13px] font-semibold px-4 py-2 rounded-lg border-none cursor-pointer hover:bg-[#6b5248] transition-colors"
          >
            + 페이지 추가
          </button>
        </div>

        <div className="bg-white rounded-2xl border border-[#e8e0dc] overflow-hidden shadow-[0_2px_12px_rgba(136,114,103,0.06)]">
          <table className="w-full">
            <thead>
              <tr className="bg-[#ede0d8] border-b border-[#e8e0dc]">
                <th className="text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[48px]">ID</th>
                <th className="text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium">제목</th>
                <th className="hidden lg:table-cell text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium">슬러그</th>
                <th className="text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[64px]">노출</th>
                <th className="hidden xl:table-cell text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[110px]">등록일</th>
                <th className="text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[60px]"></th>
              </tr>
            </thead>
            <tbody>
              {pages.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-12 text-gray-300 text-[13px]">
                    등록된 페이지가 없습니다.
                  </td>
                </tr>
              ) : pages.map(page => (
                <tr
                  key={page.id}
                  className="border-t border-[#f5f2f0] hover:bg-[#fdf8f0] transition-colors cursor-pointer"
                  onClick={() => navigate(`/admin/page/${page.id}`)}
                >
                  <td className="px-4 py-3 text-[13px] text-gray-400 whitespace-nowrap">{page.id}</td>
                  <td className="px-4 py-3 text-[13px] font-medium text-[#2a1f1a]">{page.title}</td>
                  <td className="hidden lg:table-cell px-4 py-3 text-[12px] text-gray-400 font-mono whitespace-nowrap">{page.slug}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`text-[11px] px-2 py-0.5 rounded font-medium ${
                      page.is_visible ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-400'
                    }`}>
                      {page.is_visible ? '노출' : '숨김'}
                    </span>
                  </td>
                  <td className="hidden xl:table-cell px-4 py-3 text-[12px] text-gray-400 whitespace-nowrap">{formatDate(page.created_at)}</td>
                  <td className="px-4 py-3 whitespace-nowrap" onClick={e => e.stopPropagation()}>
                    <button
                      onClick={() => handleDelete(page.id, page.title)}
                      className="text-[12px] text-red-400 hover:text-red-600 border border-red-100 rounded px-2 py-1 bg-transparent cursor-pointer whitespace-nowrap"
                    >
                      삭제
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}